"use client";

/**
 * The "Verify me" message in the Discord server, posted by CMTA_AUTOBOT.
 *
 * Volunteer Hub owns this message end to end — no webhook tool, no third-party
 * embed builder. The copy lives in backend/app/discord/announce.py; after
 * changing it, "Update all" re-applies it to every recorded post.
 */

import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import { fetchWithAuth } from "@/lib/auth";

const DARK = "#003d45";

interface Post {
    channel_id: string;
    channel_name?: string | null;
    message_id: string;
    posted_at: string;
    url: string;
}

interface Preview {
    embeds: {
        title: string;
        description: string;
        color?: number;
        image?: { url: string };
        footer?: { text: string };
    }[];
    components: { components: { label: string; url: string; emoji?: { name: string } }[] }[];
}

/**
 * Just enough of Discord's markdown for this message: **bold**, *italic*,
 * <#channel> mentions and line breaks. Built as React nodes, never HTML.
 */
function DiscordMarkdown({ text, channels }: { text: string; channels: Record<string, string> }) {
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|<#\d+>)/g);
    return (
        <>
            {parts.map((p, i) => {
                if (p.startsWith("**") && p.endsWith("**")) {
                    return <strong key={i} className="font-bold text-white">{p.slice(2, -2)}</strong>;
                }
                if (p.startsWith("<#")) {
                    const id = p.slice(2, -1);
                    return (
                        <span key={i} className="rounded bg-[#3c4270] px-1 font-medium text-[#c9cdfb]">
                            # {channels[id] ?? id}
                        </span>
                    );
                }
                if (p.length > 2 && p.startsWith("*") && p.endsWith("*")) {
                    return <em key={i}>{p.slice(1, -1)}</em>;
                }
                return <span key={i}>{p}</span>;
            })}
        </>
    );
}

async function errorText(res: Response): Promise<string> {
    const body = await res.json().catch(() => ({}));
    return typeof body?.detail === "string" ? body.detail : `Request failed (HTTP ${res.status}).`;
}

export function DiscordVerifyMessageCard() {
    const [posts, setPosts] = useState<Post[]>([]);
    const [preview, setPreview] = useState<Preview | null>(null);
    const [channelNames, setChannelNames] = useState<Record<string, string>>({});
    const [channel, setChannel] = useState("");
    const [busy, setBusy] = useState<string | null>(null); // "post" | "refresh" | message_id
    const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
    const [notice, setNotice] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

    const load = useCallback(async () => {
        try {
            const res = await fetchWithAuth("/api/v1/staff/discord/verify-message");
            if (!res.ok) throw new Error(await errorText(res));
            const json = await res.json();
            setPosts(json.posts);
            setPreview(json.preview);
            setChannelNames(json.channel_names ?? {});
        } catch (e) {
            setNotice({ kind: "error", text: e instanceof Error ? e.message : "Could not load the verify message." });
        }
    }, []);

    // Every state write follows the awaited fetch — same shape as DiscordPanel.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    useEffect(() => { void load(); }, [load]);

    const post = useCallback(async () => {
        setBusy("post");
        setNotice(null);
        try {
            const res = await fetchWithAuth("/api/v1/staff/discord/verify-message", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ channel }),
            });
            if (!res.ok) throw new Error(await errorText(res));
            setPosts((await res.json()).posts);
            setChannel("");
            setNotice({ kind: "ok", text: "Posted. Open it in Discord to check it." });
        } catch (e) {
            setNotice({ kind: "error", text: e instanceof Error ? e.message : "Could not post." });
        } finally {
            setBusy(null);
        }
    }, [channel]);

    const refresh = useCallback(async () => {
        setBusy("refresh");
        setNotice(null);
        try {
            const res = await fetchWithAuth("/api/v1/staff/discord/verify-message/refresh", { method: "POST" });
            if (!res.ok) throw new Error(await errorText(res));
            const results: { ok: boolean; error?: string }[] = (await res.json()).results;
            const failed = results.filter((r) => !r.ok);
            setNotice(
                failed.length
                    ? { kind: "error", text: `${results.length - failed.length} updated, ${failed.length} failed: ${failed[0].error}` }
                    : { kind: "ok", text: `Updated ${results.length} message${results.length === 1 ? "" : "s"}.` },
            );
        } catch (e) {
            setNotice({ kind: "error", text: e instanceof Error ? e.message : "Could not update." });
        } finally {
            setBusy(null);
        }
    }, []);

    const remove = useCallback(async (messageId: string) => {
        setBusy(messageId);
        setNotice(null);
        try {
            const res = await fetchWithAuth(`/api/v1/staff/discord/verify-message/${messageId}`, { method: "DELETE" });
            if (!res.ok) throw new Error(await errorText(res));
            setPosts((await res.json()).posts);
            setConfirmDelete(null);
        } catch (e) {
            setNotice({ kind: "error", text: e instanceof Error ? e.message : "Could not delete." });
        } finally {
            setBusy(null);
        }
    }, []);

    const embed = preview?.embeds[0];
    const buttons = preview?.components[0]?.components ?? [];

    return (
        <div className="rounded-xl border border-hairline p-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-extrabold" style={{ color: DARK }}>&ldquo;Verify me&rdquo; message</h3>
                {posts.length > 0 && (
                    <button
                        onClick={() => void refresh()}
                        disabled={busy !== null}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#666] hover:underline disabled:opacity-60"
                        title="Re-apply the current text and buttons to every posted copy"
                    >
                        {busy === "refresh" && <Loader2 className="h-3 w-3 animate-spin" />}
                        Update all
                    </button>
                )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-[#666]">
                Posted by CMTA_AUTOBOT. For a private channel, first add CMTA_AUTOBOT in the channel&apos;s
                Permissions with <b>View Channel</b>, <b>Send Messages</b> and <b>Embed Links</b>.
            </p>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
                {/* Preview, styled after Discord's dark embed */}
                {embed && (
                    <div className="rounded-lg bg-[#313338] p-3 text-[13px] text-[#dbdee1]">
                        <div
                            className="rounded border-l-4 bg-[#2b2d31] p-3"
                            style={{ borderLeftColor: embed.color != null ? `#${embed.color.toString(16).padStart(6, "0")}` : DARK }}
                        >
                            <div className="font-bold text-white">{embed.title}</div>
                            <div className="mt-2 whitespace-pre-wrap leading-snug">
                                <DiscordMarkdown text={embed.description} channels={channelNames} />
                            </div>
                            {embed.image?.url && (
                                // eslint-disable-next-line @next/next/no-img-element -- remote preview of what Discord fetches
                                <img
                                    src={embed.image.url}
                                    alt="Welcome to CMT Association banner"
                                    className="mt-3 w-full rounded"
                                />
                            )}
                            {embed.footer && <div className="mt-3 text-[11px] text-[#949ba4]">{embed.footer.text}</div>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {buttons.map((b) => (
                                <a
                                    key={b.label}
                                    href={b.url}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    className="inline-flex items-center gap-1.5 rounded bg-[#4e5058] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#6d6f78]"
                                >
                                    {b.emoji?.name && <span aria-hidden>{b.emoji.name}</span>}
                                    {b.label} <ExternalLink className="h-3 w-3" />
                                </a>
                            ))}
                        </div>
                        <p className="mt-2 text-[10px] text-[#949ba4]">
                            Discord shows link buttons in grey. Only bot-handled buttons can be blue.
                        </p>
                    </div>
                )}

                <div className="flex flex-col gap-3">
                    <label className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#666]">
                        Post to channel
                    </label>
                    <input
                        value={channel}
                        onChange={(e) => setChannel(e.target.value)}
                        placeholder="Channel ID or link"
                        className="rounded-xl border border-hairline px-3 py-2 text-sm outline-none focus:border-steel"
                    />
                    <p className="text-[11px] leading-relaxed text-[#888]">
                        In Discord: right-click the channel → <b>Copy Link</b> (or Copy Channel ID with Developer
                        Mode on).
                    </p>
                    <button
                        onClick={() => void post()}
                        disabled={!channel.trim() || busy !== null}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#5865F2] px-4 py-2 text-sm font-bold text-white hover:opacity-90 disabled:opacity-50"
                    >
                        {busy === "post" && <Loader2 className="h-4 w-4 animate-spin" />}
                        Post message
                    </button>

                    {notice && (
                        <p className={`text-xs leading-relaxed ${notice.kind === "ok" ? "text-emerald-700" : "text-[#b3261e]"}`}>
                            {notice.text}
                        </p>
                    )}

                    {posts.length > 0 && (
                        <ul className="divide-y divide-hairline rounded-lg border border-hairline text-xs">
                            {posts.map((p) => (
                                <li key={p.message_id} className="flex items-center justify-between gap-2 px-3 py-2">
                                    <a href={p.url} target="_blank" rel="noreferrer noopener" className="min-w-0 truncate font-semibold hover:underline" style={{ color: DARK }}>
                                        #{p.channel_name || p.channel_id}
                                    </a>
                                    {confirmDelete === p.message_id ? (
                                        <span className="flex shrink-0 gap-2">
                                            <button onClick={() => void remove(p.message_id)} disabled={busy !== null} className="font-bold text-[#b3261e] hover:underline disabled:opacity-60">
                                                {busy === p.message_id ? "Deleting…" : "Delete from Discord"}
                                            </button>
                                            <button onClick={() => setConfirmDelete(null)} className="text-[#888] hover:underline">Cancel</button>
                                        </span>
                                    ) : (
                                        <button onClick={() => setConfirmDelete(p.message_id)} className="shrink-0 text-[#888] hover:underline">
                                            Delete
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DiscordVerifyMessageCard;
